import { calculateShipping } from "@/api/webApi.js";
import {
  updateOrder,
  linkShippingCalculation
} from "@/api/store/OrdersService.js";
import { loadEditableQuote, totalsPayload, quoteResult } from "./quoteLines.js";

// "UPS Ground® (International)" -> "ups ground international"
const normalize = text =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Las opciones del calculador, con las de entrega local como las arma
// ShippingCalculator.vue
const optionsOf = res => [
  ...(res.shippingOptions || []).map(option => ({
    company: option.company,
    service: option.service,
    serviceCode: option.serviceCode || option.code,
    price: Number(option.price) || 0
  })),
  ...(res.localDeliveryOptions || res.local_delivery_options || [])
    .filter(option => option.eligible !== false)
    .map(option => {
      const depName =
        option.depositoName || option.deposito_name || option.depositoId;
      const depId = option.depositoId || option.deposito_id;
      return {
        company: "Local Delivery",
        service: `Local delivery from ${depName} (${option.distanceMiles ||
          option.distance_miles} mi)`,
        serviceCode: `local_delivery_${depId}`,
        price: Number(option.price) || 0
      };
    })
];

const optionLabel = option => `${option.service} — ${option.price.toFixed(2)}`;

export default {
  definition: {
    name: "quote_shipping",
    description:
      "Calcula el envío de una cotización con su dirección de envío y sus piezas (el mismo calculador del modal de la orden: UPS, FedEx, entrega local...). Con service vacío le muestra al vendedor las opciones con su costo en una tarjeta para que elija. Con service (p. ej. 'UPS Ground') pone en la cotización ese servicio con el costo calculado. Sólo cotizaciones creadas por el vendedor que te habla. Recalcúlalo si cambian las piezas o la dirección.",
    strict: true,
    input_schema: {
      type: "object",
      properties: {
        reference: {
          type: "string",
          description:
            "La cotización, p. ej. 'Q-200000005'; vacío para la de esta conversación"
        },
        service: {
          type: "string",
          description:
            "El servicio a poner, como lo dijo el vendedor ('UPS Ground', 'next day air'); vacío para sólo ver las opciones"
        }
      },
      required: ["reference", "service"],
      additionalProperties: false
    }
  },

  async run({ reference, service }, ctx) {
    const order = await loadEditableQuote(reference, ctx);
    const address = order.shippingAddress || {};
    if (!address.postalCode) {
      throw new Error(
        "La cotización no tiene dirección de envío con código postal: ponle el cliente o la dirección con update_quote antes de calcular el envío."
      );
    }
    const items = (order.items || []).filter(item => Number(item.quantity));
    if (!items.length) {
      throw new Error("La cotización no tiene piezas: no hay qué enviar.");
    }

    const res = await calculateShipping(
      items.map(item => ({
        nkrCode: item.nkrCode || item.kitCode,
        quantity: Number(item.quantity),
        price: Number(item.price)
      })),
      address.postalCode,
      {
        street1: address.street1 || "",
        street2: address.street2 || "",
        city: address.city || ""
      },
      address.country || "US",
      address.state || "",
      false,
      null,
      order.departamentoId,
      false
    );
    const options = optionsOf(res);
    const calculationId = res.calculation_id || res.calculationId || null;
    // Residencial o comercial lo detecta el API con la calle; residencial lleva recargo
    const residentialCheck = res.residential || null;
    const calcWarnings = [
      ...(res.warnings || []),
      ...(residentialCheck && residentialCheck.residential
        ? [
            residentialCheck.verified
              ? "Dirección residencial según ShipStation: las tarifas incluyen el recargo residencial del carrier"
              : "Dirección sin verificar: se cotizó como residencial (con recargo)"
          ]
        : [])
    ];
    if (!options.length) {
      throw new Error(
        `El calculador no devolvió opciones de envío para ${
          address.postalCode
        }${calcWarnings.length ? `: ${calcWarnings.join("; ")}` : ""}.`
      );
    }

    // Todas las palabras de lo pedido en el nombre del servicio
    const words = normalize(service)
      .split(" ")
      .filter(Boolean);
    const matches = words.length
      ? options.filter(option => {
          const name = normalize(
            `${option.company} ${option.service} ${option.serviceCode}`
          );
          return words.every(word => name.includes(word));
        })
      : [];

    // Sin servicio, o ambiguo ("ups" son tres): que elija en la tarjeta
    if (matches.length !== 1) {
      return {
        card: {
          type: "shippingOptions",
          reference: order.orderNumber,
          options: matches.length ? matches : options
        },
        content: JSON.stringify({
          shownToUserAsCard: true,
          applied: false,
          ...(words.length
            ? {
                note: matches.length
                  ? `"${service}" coincide con varias opciones: que el vendedor elija una`
                  : `Ninguna opción coincide con "${service}"`
              }
            : {}),
          options: (matches.length ? matches : options).map(optionLabel),
          ...(calcWarnings.length ? { warnings: calcWarnings } : {})
        })
      };
    }

    const option = matches[0];
    if (calculationId) {
      await linkShippingCalculation(order.orderNumber, calculationId, {
        service_code: option.serviceCode,
        rate: option.price,
        carrier: option.company
      });
    }
    const saved = await updateOrder(
      order.orderNumber,
      "totals",
      totalsPayload(order, {
        shippingPreference: option.service,
        shippingPaid: option.price,
        shippingSelectedServiceCarrier: option.company,
        shippingSelectedServiceCode: option.serviceCode,
        shippingSelectedServiceRate: option.price
      })
    );
    return quoteResult(order.orderNumber, ctx, {
      warnings: [...calcWarnings, ...((saved && saved.warnings) || [])]
    });
  }
};
